import React from 'react';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';
import ReportTest from '../report/report_components/ReportTest';
import ReportNote from '../report/report_components/ReportNote';
import NodeWorkbench from '../report/report_components/node_workbench/NodeWorkbench';

import CloseIcon from '@mui/icons-material/Close';
import BookIcon from '@mui/icons-material/Book';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import '../styles/lookup.scss';
import '../styles/report.scss';
import 'materialize-css';

const LOG_COMPONENT = { id: '-1', name: 'Service notebook', previous_notes: [] };

class ServiceLogModal extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      addIntent: null,
      nodeFocused: null,
    };
    this.saveNode = this.saveNode.bind(this);
  }

  componentDidMount() {
    if (!this.props.tests?.length) {
      this.props.fetchTestFields(this.props.authToken, { silent: true });
    }
  }

  // Sort date keys (M/D/YYYY) so the newest entries appear at the top.
  sortedDates() {
    const logs = this.props.logs || {};
    return Object.keys(logs).sort((a, b) => new Date(b) - new Date(a));
  }

  todayKey() {
    return new Date().toLocaleString().split(',')[0];
  }

  formatDate(key) {
    const parsed = new Date(key);
    if (Number.isNaN(parsed.getTime())) return key;
    const label = parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return key === this.todayKey() ? `Today · ${label}` : label;
  }

  entryCount() {
    return this.sortedDates().reduce((acc, date) => (
      acc + Object.keys(this.props.logs?.[date] || {}).length
    ), 0);
  }

  closeWorkbench() {
    this.setState({ addIntent: null, nodeFocused: null });
  }

  openWorkbench(intent) {
    if (this.state.addIntent === intent && !this.state.nodeFocused) {
      this.closeWorkbench();
      return;
    }
    this.setState({ addIntent: intent, nodeFocused: null });
  }

  editNote(node) {
    this.setState({ addIntent: null, nodeFocused: node });
  }

  isEditingNode(node) {
    return !this.state.addIntent
      && this.state.nodeFocused
      && String(this.state.nodeFocused.id) === String(node.id);
  }

  saveNode(node, storage, onComplete) {
    const payload = { ...node, logDate: this.todayKey() };

    if (storage?.fridge) {
      this.props.updateServiceLogLocal(payload);
    }

    if (storage?.freezer) {
      const isDraft = Number(payload.id) > 1e12;
      const emptyNote = payload.iAm !== 'test' && !payload.content?.trim() && !payload.image && !payload.deleted;
      if (isDraft && emptyNote) {
        this.props.updateServiceLogLocal({ ...payload, deleted: true });
        onComplete?.();
        return;
      }

      return this.props.updateServiceLog(
        payload,
        this.props.customerId,
        this.props.authToken,
        () => onComplete?.()
      );
    }

    onComplete?.();
  }

  renderAddNodeRow() {
    const active = this.state.addIntent;

    return (
      <div className="node-container" data-type="pseudonode">
        <div className="add-node-row">
          <button
            type="button"
            className={`add-node${active === 'test' ? ' is-active' : ''}`}
            onClick={() => this.openWorkbench('test')}
          >
            <ScienceOutlinedIcon className="add-node-icon" />
            Test
          </button>
          <button
            type="button"
            className={`add-node${active === 'note' ? ' is-active' : ''}`}
            onClick={() => this.openWorkbench('note')}
          >
            <NotesOutlinedIcon className="add-node-icon" />
            Note
          </button>
          <button
            type="button"
            className={`add-node${active === 'image' ? ' is-active' : ''}`}
            onClick={() => this.openWorkbench('image')}
          >
            <AttachFileIcon className="add-node-icon" />
            Attach
          </button>
        </div>
      </div>
    );
  }

  renderInlineEditor() {
    return (
      <div className="service-log-inline-editor">
        <NodeWorkbench
          embedded={true}
          component={LOG_COMPONENT}
          node={this.state.nodeFocused}
          saveNode={this.saveNode}
          switchFocus={(node) => this.setState({ nodeFocused: node })}
          close={() => this.closeWorkbench()}
        />
      </div>
    );
  }

  renderNodes(nodes) {
    return Object.entries(nodes || {}).map(([nodeId, node]) => {
      if (this.isEditingNode(node)) {
        return (
          <React.Fragment key={`edit-${nodeId}`}>
            {this.renderInlineEditor()}
          </React.Fragment>
        );
      }

      return node.iAm === 'test' ? (
        <ReportTest
          key={`test-${nodeId}`}
          test={node}
          component={LOG_COMPONENT}
          saveNode={this.saveNode}
          log={true}
        />
      ) : (
        <ReportNote
          key={`note-${nodeId}`}
          node={node}
          log={true}
          handleClick={() => this.editNote(node)}
        />
      );
    });
  }

  render() {
    const dates = this.sortedDates();
    const todayKey = this.todayKey();
    const todayNodes = this.props.logs?.[todayKey];
    const previousDates = dates.filter((date) => date !== todayKey);
    const count = this.entryCount();
    const adding = !!this.state.addIntent;

    return (
      <div className="modal-backdrop modal-large service-log-modal" onClick={() => this.props.close()}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="service-log-header">
              <div className="service-log-heading">
                <span className="service-log-icon-well" aria-hidden="true">
                  <BookIcon className="service-log-header-icon" />
                </span>
                <div className="service-log-copy">
                  <div className="service-log-title-row">
                    <h3>Service notebook</h3>
                    <span className="service-log-badge">Internal</span>
                  </div>
                  <span className="service-log-subtitle">
                    {count === 0
                      ? 'Staff only'
                      : `${Math.min(20, count)} recent ${count === 1 ? 'entry' : 'entries'}`}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="service-log-close"
                aria-label="Close service notebook"
                onClick={() => this.props.close()}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="service-log-body">
              <div className="service-log-day is-today">
                <h3 className="log-date">{this.formatDate(todayKey)}</h3>
                {todayNodes ? this.renderNodes(todayNodes) : (
                  <div className="service-log-empty-day">
                    {count === 0 ? 'Nothing in the notebook yet.' : 'Nothing written today.'}
                  </div>
                )}
                <div className="service-log-composer">
                  {this.renderAddNodeRow()}
                  {adding ? (
                    <NodeWorkbench
                      key={this.state.addIntent}
                      embedded={true}
                      component={LOG_COMPONENT}
                      node={this.state.nodeFocused}
                      initialOption={this.state.addIntent}
                      saveNode={this.saveNode}
                      switchFocus={(node) => this.setState({ nodeFocused: node })}
                      close={() => this.closeWorkbench()}
                    />
                  ) : null}
                </div>
              </div>

              {previousDates.map((date) => (
                <div key={date} className="service-log-day">
                  <h3 className="log-date">{this.formatDate(date)}</h3>
                  {this.renderNodes(this.props.logs[date])}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    tests: state.testFields,
    logs: state.info?.serviceLog,
    customerId: state.info?.id,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ServiceLogModal);
